/* eslint-disable react/display-name */
import React, { useState, forwardRef } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { ListItem, Button, Collapse } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { Color } from 'theme/style';

const CustomRouterLink = forwardRef((props, ref) => (
  <div
    ref={ref}
    style={{ flexGrow: 1 }}
  >
    <RouterLink {...props} />
  </div>
));

const useStyles = makeStyles(theme => ({
  item: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    flexDirection: 'column',
    margin: '0px',
    marginTop: 10,
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    flexDirection: 'column',
    margin: '0px',
    marginTop: 10,
  },
  button: {
    color: 'rgba(255,255,255,1)',
    height: '6rem',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: -0.2,
    fontSize: '1.4rem',
    width: '80px',
    fontWeight: 500,
    borderRadius: '1.6rem',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.15)',
      '& $icon': {
      }
    },
    '&>span': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  buttonLeaf: {
    color: 'rgba(255,255,255,0.5)',
    height: '3.3rem',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: -0.2,
    fontSize: '1.4rem',
    width: '100%',
    fontWeight: 400,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.05)',
      '& $icon': {
      }
    },
    '&>span': {
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'center'
    },
  },
  icon: {
    color: 'rgba(255,255,255,0.3)',
    display: 'flex',
    alignItems: 'center',
    width: 30,
    fontSize: '1.8rem'
  },
  expandIcon: {
    display: 'none',
    marginLeft: 'auto',
    marginRight: 10,
    height: 16,
    width: 16
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    marginLeft: 'auto',
  },
  active: {
    backgroundColor: 'rgba(255,255,255,0.25)',
    color: theme.palette.common.white,
    fontWeight: 500,
    '& $icon': {
      color: theme.palette.common.white,
    }
  }
}));

const NavigationListItem = props => {
  const {
    title,
    href,
    depth,
    children,
    icon: Icon,
    className,
    open: openProp,
    label: Label,
    ...rest
  } = props;

  const classes = useStyles();
  const [open, setOpen] = useState(openProp);

  const handleToggle = () => {
    setOpen(open => !open);
    if (props.onClick != null) props.onClick()
  };

  let marginLeft = 0;
  let paddingLeft = 10;

  if (depth > 0) {
    marginLeft = 0;
    paddingLeft = 10
  }

  const style = {
    marginLeft,
    paddingLeft
  };

  if (children) {
    return (
      <ListItem
        {...rest}
        className={clsx(classes.item, className)}
        disableGutters
      >
        <Button
          className={classes.button}
          onClick={handleToggle}
          style={style}
        >
          {Icon && <Icon className={classes.icon} />}
          {title}
          {open ? (
            <ExpandLessIcon
              className={classes.expandIcon}
              color="inherit"
            />
          ) : (
            <ExpandMoreIcon
              className={classes.expandIcon}
              color="inherit"
            />
          )}
        </Button>
        <Collapse in={open} className={classes.collapse}>{children}</Collapse>
      </ListItem>
    );
  } else {
    let itemClass = classes.itemLeaf
    let buttonClass = classes.button
    if (depth > 0) buttonClass = classes.buttonLeaf

    return (
      <ListItem
        {...rest}
        className={clsx(classes.itemLeaf, className)}
        disableGutters
      >
        <Button
          activeClassName={classes.active}
          className={clsx(buttonClass, `depth-${depth}`)}
          component={CustomRouterLink}
          style={style}
          onClick={props.onItemClick}
          to={href}
        >
          {Icon && <Icon className={classes.icon} />}
          {title}
          {Label && (
            <span className={classes.label}>
              <Label />
            </span>
          )}
        </Button>
      </ListItem>
    );
  }
};

NavigationListItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.any,
  label: PropTypes.any,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  onItemClick: PropTypes.any
};

NavigationListItem.defaultProps = {
  depth: 0,
  open: false
};

export default NavigationListItem;
